import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Send, Mail } from "lucide-react";
import { toast } from "../ui/sonner";
import { PdfPreviewDialog } from "../pdf/PdfPreviewDialog";

/* 已签约客户 Statement：预览 → 下载 / 打印 / 一键邮件给客户（附 PDF，记入收款沟通记录）。 */
export function StatementPreviewDialog({ api, leadId, clientName, zh, onClose, onSent }) {
  const [sendOpen, setSendOpen] = useState(false);
  return (
    <PdfPreviewDialog api={api} url={`/crm/signed/${leadId}/statement.pdf`} title={`Statement · ${clientName || leadId}`} fileName={`Statement_${(clientName || leadId).replace(/[^\w\u4e00-\u9fff-]+/g, "_")}.pdf`} zh={zh} onClose={onClose} testId="statement"
      description={zh ? "先核对预览内容，确认无误后再下载、打印，或一键邮件给客户。" : "Review the statement first, then download, print, or email it to the customer."}
      actions={<Button variant="outline" size="sm" onClick={() => setSendOpen((v) => !v)} className="border-brand-200 text-brand-700" data-testid="statement-send-toggle"><Mail className="h-3.5 w-3.5 mr-1" />{zh ? "发送给客户" : "Send to customer"}</Button>}
      below={sendOpen && <StatementSendPanel api={api} leadId={leadId} zh={zh} onSent={() => { setSendOpen(false); onSent?.(); }} />} />
  );
}

function StatementSendPanel({ api, leadId, zh, onSent }) {
  const [pv, setPv] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/crm/signed/${leadId}/statement-preview`).then((r) => { setPv(r.data); setSubject(r.data.subject || ""); setMessage(r.data.message || ""); })
      .catch((e) => toast.error(e?.response?.data?.detail || e.message));
  }, [api, leadId]);
  const send = async () => {
    if (!window.confirm(zh ? `确认把 Statement PDF 邮件发送给 ${pv?.to?.email}？` : `Email the statement PDF to ${pv?.to?.email}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/signed/${leadId}/statement-send`, { subject, message });
      toast.success(zh ? `已发送至 ${data.to}` : `Sent to ${data.to}`);
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  if (!pv) return <p className="text-xs text-slate-400" data-testid="statement-send-loading">{zh ? "载入话术…" : "Loading…"}</p>;
  const can = pv.hasEmail && pv.emailConfigured;
  return (
    <div className="rounded-xl border border-brand-100 bg-brand-50/40 p-3 space-y-2" data-testid="statement-send-panel">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-slate-700">{zh ? "收件邮箱" : "To"}</span>
        <span className={pv.hasEmail ? "text-slate-800" : "text-red-600"} data-testid="statement-send-to">{pv.to?.email || (zh ? "客户无邮箱 — 请先在线索资料补充" : "No email on file — add it to the lead first")}</span>
        {!pv.emailConfigured && <span className="text-red-600" data-testid="statement-send-noemail">{zh ? "邮件服务未配置" : "Email service not configured"}</span>}
      </div>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white" data-testid="statement-send-subject" />
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="statement-send-message" />
      <div className="flex justify-end">
        <Button size="sm" disabled={!can || busy} onClick={send} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="statement-send-submit"><Send className="h-3.5 w-3.5 mr-1" />{busy ? (zh ? "发送中…" : "Sending…") : (zh ? "邮件发送（附 Statement PDF）" : "Send email (PDF attached)")}</Button>
      </div>
    </div>
  );
}
